import React, { forwardRef } from 'react'
import { cn } from '@/utils/cn'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  onRightIconClick?: () => void
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      onRightIconClick,
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const inputId = id ?? `input-${Math.random().toString(36).slice(2, 9)}`

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-[var(--text-primary)]"
          >
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leftIcon && (
            <span className="absolute left-3 text-[var(--text-muted)] flex items-center pointer-events-none">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'w-full rounded-[var(--radius-md)] py-2.5 text-sm',
              'bg-[var(--glass-light)] text-[var(--text-primary)]',
              'border border-[var(--border-medium)]',
              'placeholder:text-[var(--text-muted)]',
              'transition-all duration-150',
              'focus:outline-none focus:border-[var(--color-brand)] focus:ring-2 focus:ring-[rgba(var(--color-brand-rgb),0.15)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              leftIcon ? 'pl-10 pr-4' : 'px-4',
              rightIcon ? 'pr-10' : '',
              error && 'border-[var(--color-error)] focus:border-[var(--color-error)] focus:ring-[rgba(239,68,68,0.15)]',
              className,
            )}
            {...props}
          />
          {rightIcon && (
            <button
              type="button"
              onClick={onRightIconClick}
              className={cn(
                'absolute right-3 text-[var(--text-muted)] flex items-center',
                'transition-colors duration-150',
                onRightIconClick
                  ? 'cursor-pointer hover:text-[var(--text-primary)]'
                  : 'pointer-events-none',
              )}
              tabIndex={onRightIconClick ? 0 : -1}
              aria-label="toggle input action"
            >
              {rightIcon}
            </button>
          )}
        </div>
        {error && (
          <p className="text-xs text-[var(--color-error)] flex items-center gap-1">
            {error}
          </p>
        )}
        {hint && !error && (
          <p className="text-xs text-[var(--text-muted)]">{hint}</p>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'
